/**
 * Urmarirea comenzii.
 *
 * Codul comenzii (ADR-022) e identificatorul pe care il rostesc clientul,
 * restaurantul si curierul. E stabil pe toata viata comenzii si e tiparit si pe
 * bonul de bucatarie, deci pagina il afiseaza mare, nu ca detaliu tehnic.
 *
 * Ce vede clientul, in ordinea in care intreaba: in ce stadiu e comanda (pasi
 * numiti, nu coduri), cand ajunge, cine o aduce (cu buton de apel de indata ce
 * curierul a preluat-o), apoi — pliate — ce a comandat, cat plateste, de la
 * cine cumpara si istoricul complet.
 *
 * Doua defecte reparate mai demult si pastrate:
 *  - G2: ruta e `POST /api/orders/:code/urmarire` si cere TELEFONUL comenzii.
 *    Cine vine din confirmare nu e intrebat nimic: dispozitivul a retinut
 *    perechea. Cine deschide linkul altundeva isi introduce telefonul o data.
 *  - G5: o eroare de transport NU devine "Comanda nu a putut fi gasita" si nu
 *    sterge ecranul. Ultima stare primita ramane afisata, cu o banda.
 */

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { CORE_ERROR_CODES, isTerminalPlatformOrderStatus, type OrderTrackingDto } from "@deliveryapp/shared";

import { fetchOrderTracking } from "../api/client";
import { useBootstrap } from "../components/BootstrapProvider";
import { cartLinesFromRemembered, readRememberedOrders, recallOrderPhone, rememberOrder, useCart } from "../components/CartProvider";
import { EtaWindow, formatClock } from "../components/EtaWindow";
import { paymentMethodLabel, pickupMapsUrl } from "../components/fulfillment";
import { IconCheck, IconChef, IconPhone, IconPin, IconReceipt, IconRepeat, IconStore, IconTruck } from "../components/icons";
import { OrderStatusBadge, OrderTimeline, stopStatusLabel } from "../components/OrderTimeline";
import { SellerIdentityCard } from "../components/SellerIdentityCard";
import { ErrorState, LoadingState } from "../components/StateScreen";
import { useToast } from "../components/ToastProvider";
import { TotalsPanel } from "../components/TotalsPanel";
import { useLoadable } from "../components/useLoadable";
import { Alert, Button, Field, SectionHeading } from "../components/ui";

const POLL_INTERVAL_MS = 20_000;

function countDigits(value: string): number {
  return value.replace(/\D/g, "").length;
}

/**
 * Cei patru pasi pe care ii intelege clientul. Statusurile interne se
 * proiecteaza pe ei; un status necunoscut (server mai nou) nu avanseaza nimic.
 */
const STEPS = [
  { key: "received", label: "Primită", hint: "Restaurantul a primit comanda" },
  { key: "preparing", label: "Se pregătește", hint: "Bucătăria lucrează la comanda ta" },
  { key: "on_the_way", label: "În drum", hint: "Curierul a preluat comanda" },
  { key: "delivered", label: "Livrată", hint: "Poftă bună!" },
] as const;

/** La ridicare nu exista drum: pasul al treilea e „gata", al patrulea „ridicata". */
const PICKUP_STEPS = [
  { key: "received", label: "Primită", hint: "Restaurantul a primit comanda" },
  { key: "preparing", label: "Se pregătește", hint: "Bucătăria lucrează la comanda ta" },
  { key: "ready", label: "Gata", hint: "Te așteptăm la restaurant" },
  { key: "picked_up", label: "Ridicată", hint: "Poftă bună!" },
] as const;

function stepIndexFor(status: string, pickup = false): number {
  if (pickup) {
    if (status === "ready") return 2;
    if (status === "picked_up" || status === "in_transit" || status === "delivered") return 3;
  }
  switch (status) {
    case "quoting":
    case "committed":
    case "awaiting_assignment":
    case "assigned":
      return 0;
    case "in_kitchen":
    case "ready":
      return 1;
    case "picked_up":
    case "in_transit":
      return 2;
    case "delivered":
      return 3;
    default:
      return -1;
  }
}

function StatusStepper({ status, pickup = false }: { status: string; pickup?: boolean }) {
  const current = stepIndexFor(status, pickup);
  const failed = status === "failed" || status === "cancelled";
  return (
    <ol className="grid grid-cols-4 gap-1" aria-label="Stadiul comenzii">
      {(pickup ? PICKUP_STEPS : STEPS).map((step, index) => {
        const done = !failed && current > index;
        const active = !failed && current === index;
        return (
          <li key={step.key} className="flex flex-col items-center gap-1.5 text-center">
            <span
              className={[
                "grid h-9 w-9 place-items-center rounded-full border-2 text-sm font-extrabold transition-colors",
                done ? "border-success bg-success text-white" : active ? "border-brand bg-brand text-brand-contrast" : "border-line bg-surface text-ink-muted",
              ].join(" ")}
              aria-current={active ? "step" : undefined}
            >
              {done ? <IconCheck size={18} strokeWidth={2.6} /> : index + 1}
            </span>
            <span className={`text-xs font-semibold ${done || active ? "text-ink" : "text-ink-muted"}`}>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function OrderTracking() {
  const { code = "" } = useParams<{ code: string }>();
  const { marketKey, config, bootstrap } = useBootstrap();
  const { restoreOrder } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [phone, setPhone] = useState<string | null>(() => recallOrderPhone(marketKey, code));
  const [phoneDraft, setPhoneDraft] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [lastKnown, setLastKnown] = useState<OrderTrackingDto | null>(null);
  const [lastKnownAt, setLastKnownAt] = useState<string | null>(null);

  const remembered = useMemo(() => readRememberedOrders(marketKey).find((order) => order.code === code) ?? null, [marketKey, code]);

  const tracking = useLoadable(
    (signal) =>
      phone === null
        ? Promise.reject(new Error("Urmărirea are nevoie de telefonul comenzii."))
        : fetchOrderTracking(marketKey, code, phone, signal),
    [marketKey, code, phone],
    { keepDataWhileReloading: true },
  );

  const trackingData = tracking.data;
  useEffect(() => {
    if (trackingData === null) return;
    setLastKnown(trackingData);
    setLastKnownAt(new Date().toISOString());
    if (phone !== null && recallOrderPhone(marketKey, code) !== phone) {
      rememberOrder(marketKey, code, phone);
    }
  }, [trackingData, marketKey, code, phone]);

  const shown = trackingData ?? lastKnown;
  const status = shown?.status ?? null;
  const finished = status !== null && isTerminalPlatformOrderStatus(status);
  const reload = tracking.reload;

  useEffect(() => {
    if (phone === null || finished || tracking.error !== null) return;
    const timer = window.setInterval(() => reload(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [phone, finished, tracking.error, reload]);

  const submitPhone = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (countDigits(phoneDraft) < 9) {
      setPhoneError("Introdu numărul de telefon cu care ai plasat comanda.");
      return;
    }
    setPhoneError(null);
    setLastKnown(null);
    setLastKnownAt(null);
    setPhone(phoneDraft.trim());
  };

  const reorder = (): void => {
    if (remembered === null || typeof remembered.storefrontSlug !== "string") return;
    const lines = cartLinesFromRemembered(remembered.lines ?? []);
    if (lines.length === 0) return;
    const storefront = bootstrap.storefronts.find((item) => item.slug === remembered.storefrontSlug) ?? null;
    restoreOrder(remembered.storefrontSlug, storefront?.name ?? remembered.storefrontName ?? null, lines);
    toast({ key: "reorder", title: "Produsele sunt din nou în coș", description: "Le verificăm la adresa ta înainte de finalizare." });
    navigate("/cos");
  };
  const canReorder =
    remembered !== null &&
    typeof remembered.storefrontSlug === "string" &&
    (remembered.lines ?? []).length > 0 &&
    bootstrap.storefronts.some((item) => item.slug === remembered.storefrontSlug);

  if (phone === null) {
    return (
      <form onSubmit={submitPhone} className="mx-auto flex max-w-readable flex-col gap-4" noValidate>
        <SectionHeading level={1}>{`Comanda ${code}`}</SectionHeading>
        <p className="text-sm text-ink-muted">
          Ca să afișăm starea comenzii, avem nevoie de numărul de telefon cu care a fost plasată. Datele comenzii nu se arată pe baza codului singur.
        </p>
        <Field label="Telefonul comenzii" required type="tel" inputMode="tel" autoComplete="tel" value={phoneDraft} error={phoneError} onChange={(event) => setPhoneDraft(event.target.value)} />
        <Button type="submit" block size="lg">
          Vezi starea comenzii
        </Button>
      </form>
    );
  }

  const notFoundError = tracking.error !== null && tracking.error.code === CORE_ERROR_CODES.ORDER_NOT_FOUND ? tracking.error : null;

  if (notFoundError !== null) {
    return (
      <div className="mx-auto flex max-w-readable flex-col gap-4">
        <ErrorState title="Comanda nu a putut fi găsită" error={notFoundError} />
        <Button
          variant="secondary"
          onClick={() => {
            setPhoneDraft("");
            setPhone(null);
          }}
        >
          Încearcă alt număr de telefon
        </Button>
      </div>
    );
  }

  if (shown === null) {
    if (tracking.error !== null) {
      return <ErrorState title="Starea comenzii nu a putut fi încărcată" error={tracking.error} onRetry={tracking.reload} />;
    }
    return <LoadingState label="Se încarcă starea comenzii" />;
  }

  const order = shown;
  const staleError = tracking.error;
  const deliveredEntry = [...order.timeline].reverse().find((entry) => entry.status === "delivered");
  const isPickup = order.fulfillmentType === "pickup";
  const pickupPoint = order.pickupPoint ?? null;
  const step = stepIndexFor(order.status, isPickup);
  const failed = order.status === "failed" || order.status === "cancelled";
  const courier = isPickup ? null : (order.courier ?? null);
  const paymentLabel = order.paymentMethod === undefined ? null : paymentMethodLabel(order.paymentMethod);
  const headline = failed
    ? order.status === "cancelled"
      ? "Comanda a fost anulată"
      : isPickup
        ? "Comanda nu a putut fi predată"
        : "Comanda nu a putut fi livrată"
    : isPickup
      ? step === 3
        ? "Ai ridicat comanda. Poftă bună!"
        : step === 2
          ? "Comanda e gata — te așteptăm"
          : step === 1
            ? "Bucătăria pregătește comanda"
            : "Comanda a ajuns la restaurant"
      : order.status === "delivered"
        ? "Comanda a fost livrată"
        : step === 2
          ? "Curierul e în drum spre tine"
          : step === 1
            ? "Bucătăria pregătește comanda"
            : "Comanda a ajuns la restaurant";

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">{`Comanda ${order.platformOrderCode}`}</p>
          <OrderStatusBadge status={order.status} pickup={isPickup} />
        </div>
        <h1 className="text-2xl font-extrabold text-ink sm:text-3xl">{headline}</h1>
        {failed ? null : <StatusStepper status={order.status} pickup={isPickup} />}
      </header>

      {staleError === null ? null : (
        <Alert tone="warning" title="Starea afișată nu a putut fi actualizată" actions={<Button size="sm" onClick={tracking.reload}>Încearcă din nou</Button>}>
          <p>{staleError.message}</p>
          <p className="mt-1">
            {lastKnownAt === null
              ? "Mai jos e ultima stare primită."
              : `Mai jos e starea primită la ${formatClock(lastKnownAt, config.hours.timezone)}. Între timp comanda poate fi mai avansată.`}
          </p>
        </Alert>
      )}

      {failed ? null : <EtaWindow eta={order.eta} timezone={config.hours.timezone} deliveredAt={deliveredEntry?.at ?? null} pickup={isPickup} />}

      {/* Ridicarea: unde vine clientul, cum ajunge si cu ce plateste. */}
      {!isPickup || pickupPoint === null ? null : (
        <section className="da-card flex flex-col gap-3 p-4" aria-labelledby="ridicare-titlu">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
              <IconStore size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="ridicare-titlu" className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Ridici de la</h2>
              <p className="text-base font-bold text-ink">{pickupPoint.name}</p>
              <p className="text-sm text-ink-muted">{pickupPoint.address}</p>
              {paymentLabel === null ? null : <p className="mt-1 text-sm text-ink">{`Plată: ${paymentLabel}`}</p>}
            </div>
          </div>
          {step === 3 ? null : (
            <div className="flex flex-wrap gap-2">
              <a
                className="da-press inline-flex min-h-11 items-center gap-2 rounded-control bg-brand px-4 py-2.5 text-sm font-bold text-brand-contrast"
                href={pickupMapsUrl(pickupPoint)}
                target="_blank"
                rel="noreferrer"
              >
                <IconPin size={18} /> Drum până acolo
              </a>
              {pickupPoint.phone === null || pickupPoint.phone === undefined || pickupPoint.phone.trim().length === 0 ? null : (
                <a
                  className="da-press inline-flex min-h-11 items-center gap-2 rounded-control border border-line px-4 py-2.5 text-sm font-semibold text-ink"
                  href={`tel:${pickupPoint.phone.replace(/\s+/g, "")}`}
                >
                  <IconPhone size={18} /> Sună restaurantul
                </a>
              )}
            </div>
          )}
          {step === 3 ? null : (
            <p className="text-xs text-ink-muted">{`La restaurant spui codul comenzii: ${order.platformOrderCode}.`}</p>
          )}
        </section>
      )}

      {/* ADR-032: nota pusă de suport — o întârziere anunțată (fereastra de mai sus e deja mutată) și/sau un mesaj. */}
      {order.notice && (order.notice.message || order.notice.delayMinutes) ? (
        <Alert
          tone="warning"
          title={order.notice.delayMinutes ? `Livrarea întârzie cu aproximativ ${order.notice.delayMinutes} minute` : "Mesaj de la echipa de suport"}
        >
          <p>{order.notice.message ?? "Ne pare rău pentru așteptare. Fereastra de livrare de mai sus a fost actualizată."}</p>
        </Alert>
      ) : null}

      {courier === null ? null : (
        <section className="da-card flex items-center gap-3 p-4" aria-labelledby="curier-titlu">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
            <IconTruck size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="curier-titlu" className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Curier</h2>
            <p className="text-base font-bold text-ink">{courier.displayName}</p>
          </div>
          {courier.phone === null || courier.phone === undefined ? null : (
            <a className="da-press inline-flex min-h-11 items-center gap-2 rounded-control bg-brand px-4 py-2.5 text-sm font-bold text-brand-contrast" href={`tel:${courier.phone.replace(/\s+/g, "")}`}>
              <IconPhone size={18} /> Sună
            </a>
          )}
        </section>
      )}

      {canReorder ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<IconRepeat size={18} />} onClick={reorder}>
            Comandă din nou
          </Button>
          <Link to="/comenzi" className="da-press inline-flex min-h-11 items-center gap-2 rounded-control px-4 py-2.5 text-sm font-semibold text-ink-muted hover:text-ink">
            <IconReceipt size={18} /> Toate comenzile
          </Link>
        </div>
      ) : null}

      <details className="da-card" open={order.status === "delivered"}>
        <summary className="da-tap flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-bold text-ink">
          <IconReceipt size={18} className="text-ink-muted" /> Detaliile comenzii
        </summary>
        <div className="flex flex-col gap-4 border-t border-line p-4">
          {remembered === null || (remembered.lines ?? []).length === 0 ? null : (
            <ul className="divide-y divide-line text-sm">
              {(remembered.lines ?? []).map((line) => (
                <li key={line.lineId} className="flex justify-between gap-3 py-2">
                  <span className="text-ink">
                    <span className="mr-2 font-bold tabular-nums">{line.qty}×</span>
                    {line.name}
                    {line.optionLabels.length === 0 ? null : <span className="block text-xs text-ink-muted">{line.optionLabels.join(" · ")}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <TotalsPanel totals={order.totals} sellerCount={order.sellers.length} pickup={isPickup} />
          <SellerIdentityCard sellers={order.sellers} supportPhone={config.identity.supportPhone} />
        </div>
      </details>

      {order.stops === undefined || order.stops.length === 0 ? null : (
        <section className="flex flex-col gap-2" aria-labelledby="opriri-titlu">
          <h2 id="opriri-titlu" className="text-sm font-bold text-ink">Traseu</h2>
          <ul className="flex flex-col gap-2">
            {order.stops.map((stop, index) => (
              <li key={`${stop.kind}-${index}`} className="da-card flex items-center justify-between gap-3 p-3 text-sm">
                <span className="inline-flex items-center gap-2 text-ink">
                  {stop.kind === "pickup" ? <IconChef size={18} className="text-ink-muted" /> : <IconTruck size={18} className="text-ink-muted" />}
                  {stop.kind === "pickup" ? "Ridicare de la restaurant" : "Livrare la tine"}
                </span>
                <span className="text-ink-muted">
                  {stopStatusLabel(stop.status)}
                  {stop.plannedAt === null || stop.plannedAt === undefined ? "" : ` · ${formatClock(stop.plannedAt, config.hours.timezone)}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <details className="da-card">
        <summary className="da-tap cursor-pointer px-4 py-3 text-sm font-bold text-ink">Istoric</summary>
        <div className="border-t border-line p-4">
          <OrderTimeline entries={order.timeline} timezone={config.hours.timezone} />
        </div>
      </details>

      {config.identity.supportPhone === undefined ? null : (
        <p className="text-sm text-ink-muted">
          Ai nevoie de ajutor? Sună la{" "}
          <a className="font-semibold underline" href={`tel:${config.identity.supportPhone.replace(/\s+/g, "")}`}>
            {config.identity.supportPhone}
          </a>{" "}
          și spune codul comenzii: <span className="font-semibold text-ink">{order.platformOrderCode}</span>.
        </p>
      )}
    </div>
  );
}
